import { Figure } from "@/components/Figure";
import { Iframe } from "@/components/Iframe";
import {
  LightBoxContext,
  LightBoxExtension,
} from "@/components/Lightbox/LightBoxContext";
import {
  Anchor,
  Blockquote,
  Code,
  Divider,
  List,
  Mark,
  Text,
  Title,
} from "@mantine/core";
import { IconExternalLink, IconLink } from "@tabler/icons-react";
import type { JSONContent } from "@tiptap/core";
import dynamic from "next/dynamic";
import React from "react";

let index = 0;

const Carousel = dynamic(() =>
  import("../components/Carousel/Carousel").then((mod) => mod.Carousel)
);

const Slide = dynamic(() =>
  import("@mantine/carousel").then((mod) => mod.Carousel.Slide)
);

/** Рендерит из формата редактора в реакт компоненты */
export function renderJSON(node: JSONContent | undefined): React.ReactNode {
  if (typeof node === "undefined" || node.type === void 0) {
    return null;
  }

  if (node.type === "doc") {
    return node.content?.map((item) => renderJSON(item));
  }

  if (node.type === "text") {
    if (node.marks && node.marks.length > 0) {
      return node.marks.reduce<React.ReactNode>((accumulator, mark) => {
        switch (mark.type) {
          case "subscript":
            return <sub key={++index}>{accumulator}</sub>;
          case "superscript":
            return <sup key={++index}>{accumulator}</sup>;
          case "strike":
            return <s key={++index}>{accumulator}</s>;
          case "italic":
            return <em key={++index}>{accumulator}</em>;
          case "bold":
            return <strong key={++index}>{accumulator}</strong>;
          case "underline":
            return <u key={++index}>{accumulator}</u>;
          case "code":
            return <Code key={++index}>{accumulator}</Code>;
          case "highlight":
            return <Mark key={++index}>{accumulator}</Mark>;
          case "textStyle":
            return (
              <span key={++index} style={mark.attrs}>
                {accumulator}
              </span>
            );
          case "link":
            return (
              <Anchor
                href={mark.attrs?.href}
                rel={mark.attrs?.rel}
                target={mark.attrs?.target}
                key={++index}
              >
                {accumulator}
                {mark.attrs?.target === "_blank" && (
                  <IconExternalLink
                    size="0.9rem"
                    style={{ marginLeft: "2px" }}
                  />
                )}
              </Anchor>
            );

          default:
            throw new Error(`unknown mark type ${JSON.stringify(mark)}`);
        }
      }, node.text);
    }

    return node.text;
  }

  if (node.type === "heading") {
    const { level, textAlign, ["data-id"]: id } = node.attrs || {};

    return (
      <Title order={level} ta={textAlign} key={++index} id={id}>
        {node.content?.map((item) => renderJSON(item))}
        {id && (
          <Anchor href={`#${id}`} title={`Ссылка на ${id}`}>
            <IconLink />
          </Anchor>
        )}
      </Title>
    );
  }

  if (node.type === "hardBreak") {
    return <br key={++index} />;
  }

  if (node.type === "paragraph") {
    if (!node.content || node.content.length === 0) {
      return <br key={++index} />;
    }

    const { textAlign } = node.attrs || {};

    const justifyStyleFix = {
      ":after": {
        content: "''",
        display: "inline-block",
        width: "100%",
      },
    };

    /** Если у нас есть Figure тогда для текста будем использовать div
     *  иначе p (т.к по семантике нельзя вставлять figure внутри p)
     */
    const isSomeContainsFigure = isSomeNodeType(node.content, "imageResize");

    return (
      <Text
        component={isSomeContainsFigure ? "div" : "p"}
        ta={textAlign}
        key={++index}
        style={textAlign === "justify" ? justifyStyleFix : {}}
      >
        {node.content?.map((item) => renderJSON(item))}
      </Text>
    );
  }

  if (node.type === "imageResize") {
    return <Figure {...node.attrs} key={++index} />;
  }

  if (node.type === "blockquote") {
    return (
      <Blockquote cite={node.attrs?.cite} key={++index} my="md" py="md" px="lg">
        {node.content?.map((item) => renderJSON(item))}
      </Blockquote>
    );
  }

  if (node.type === "bulletList") {
    return (
      <List key={++index}>{node.content?.map((item) => renderJSON(item))}</List>
    );
  }

  if (node.type === "orderedList") {
    return (
      <List key={++index} type="ordered">
        {node.content?.map((item) => renderJSON(item))}
      </List>
    );
  }

  if (node.type === "listItem") {
    return (
      <List.Item key={++index}>
        {node.content?.map((item) => renderJSON(item))}
      </List.Item>
    );
  }

  if (node.type === "horizontalRule") {
    return <Divider key={++index} />;
  }

  if (node.type === "carousel") {
    return (
      <LightBoxContext.Provider
        value={new LightBoxExtension("carousel")}
        key={++index}
      >
        <Carousel>{node.content?.map((item) => renderJSON(item))}</Carousel>
      </LightBoxContext.Provider>
    );
  }

  if (node.type === "carouselItem") {
    const { height = "auto" } = node.attrs || {};

    return (
      <Slide key={++index} style={{ height }}>
        {node.content?.map((item) => renderJSON(item))}
      </Slide>
    );
  }

  if (node.type === "iframe") {
    return (
      <Iframe
        key={++index}
        allowFullScreen={false}
        src={node.attrs?.src}
        style={{
          height: node.attrs?.height,
        }}
      />
    );
  }

  throw new Error(`unknown node ${JSON.stringify(node)}`);
}

/** ф-я проходит по всем потомкам ноды и проверяет, если ли хотябы один элемент node.type === type */
const isSomeNodeType = (
  node: JSONContent | JSONContent[] | undefined,
  type: string
): boolean => {
  if (Array.isArray(node)) {
    return node.some((item) => isSomeNodeType(item, type));
  }

  if (node?.type === type) {
    return true;
  }

  if (node?.content) {
    return isSomeNodeType(node.content, type);
  }

  return false;
};
